import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";

const LAST_UPDATED = "February 17, 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={36} withWordmark />
        </Link>
        <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900">Back to home</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="privacy-page">
        <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">Privacy Policy</h1>
        <p className="text-slate-500 mt-3 text-sm">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate mt-10 max-w-none">
          <Section title="1. Overview">
            <p>
              This Privacy Policy explains how WindowScreenQuote.com (&ldquo;<strong>we</strong>&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses, and shares information when you use our website and platform (the &ldquo;<strong>Service</strong>&rdquo;).
            </p>
            <p>
              By using the Service you agree to this Policy. If you do not agree, please do not use the Service.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>We collect three categories of information:</p>
            <ul>
              <li>
                <strong>Information you give us.</strong>
                <ul>
                  <li><em>Homeowners</em>: name, email, phone number, address or ZIP code, and details about the screen job you want quoted (services, quantities, mesh choice, notes, etc.).</li>
                  <li><em>Screen Companies</em>: business name, owner name, email, phone, business address, logo, pricing and service configuration, mesh images, and the billing information you provide to our payment processor.</li>
                </ul>
              </li>
              <li>
                <strong>Information collected automatically.</strong> When you visit the Service we collect technical data such as IP address, device and browser type, pages viewed, referring URL, approximate location derived from IP, and timestamps. We also collect funnel events (for example, &ldquo;funnel_started&rdquo;, &ldquo;quote_viewed&rdquo;, &ldquo;contact_submitted&rdquo;) so Screen Companies can understand how their quote funnel is performing.
              </li>
              <li>
                <strong>Information from third parties.</strong> We receive limited information from our payment processor (Stripe) about subscription status and payment events, and from our email provider (Resend) about delivery status of transactional emails.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use information">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Generate the quote you request and route it to the appropriate Screen Company.</li>
              <li>Let the Screen Company contact you about your quote (by phone, text, or email).</li>
              <li>Operate, secure, and improve the Service, including funnel analytics for each Screen Company.</li>
              <li>Process subscription payments and prevent fraud.</li>
              <li>Send transactional messages such as account confirmations, quote notifications, billing receipts, and security alerts.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
          </Section>

          <Section title="4. How we share information">
            <p>We share information only as needed to operate the Service:</p>
            <ul>
              <li><strong>With Screen Companies.</strong> When you submit a quote request through a Screen Company&rsquo;s funnel, your contact information and job details are shared with that Screen Company so they can follow up and complete the job.</li>
              <li><strong>With service providers.</strong> We use trusted vendors to host the Service, process payments (Stripe), send email (Resend), and store uploaded images. They access information only to perform their services for us and are required to protect it.</li>
              <li><strong>For legal reasons.</strong> We may disclose information if required by law, subpoena, or other legal process, or to protect the rights, property, or safety of WindowScreenQuote, our users, or the public.</li>
              <li><strong>Business transfers.</strong> If WindowScreenQuote is involved in a merger, acquisition, or sale of assets, information may be transferred as part of that transaction, subject to this Policy.</li>
            </ul>
            <p>
              We do <strong>not</strong> sell your personal information. We do not share homeowner contact information with Screen Companies other than the one whose funnel you used.
            </p>
          </Section>

          <Section title="5. How long we keep information">
            <p>
              We keep your information for as long as your account is active or as needed to provide the Service. Quote and lead records are retained so Screen Companies can manage their pipeline and so we can comply with legal, accounting, and tax obligations. You can request deletion as described below; we may keep limited information to satisfy legal or fraud-prevention obligations.
            </p>
          </Section>

          <Section title="6. Your choices and rights">
            <ul>
              <li><strong>Update or correct.</strong> You can update most of your account information from within the Service. Homeowners can email us to update or delete their information.</li>
              <li><strong>Access, deletion, portability.</strong> Depending on where you live (including California and other jurisdictions with privacy laws such as the CCPA/CPRA, or the GDPR in the EEA/UK), you may have the right to access, correct, delete, restrict processing of, or receive a copy of your personal information. Submit requests to <a href="mailto:privacy@windowscreenquote.com">privacy@windowscreenquote.com</a>.</li>
              <li><strong>Marketing.</strong> We do not send unsolicited marketing emails to homeowners. Screen Companies may opt out of non-essential marketing emails from us at any time.</li>
              <li><strong>Do Not Track.</strong> The Service currently does not respond to Do Not Track browser signals.</li>
            </ul>
          </Section>

          <Section title="7. SMS / text messaging communications">
            <p>
              When you provide your mobile number to WindowScreenQuote.com — for example by completing a quote request, creating a Screen Company account, or opting in on any of our forms — you may receive transactional text messages from WindowScreenQuote.com regarding:
            </p>
            <ul>
              <li>Quote request confirmations and status updates;</li>
              <li>Appointment coordination and scheduling reminders;</li>
              <li>Customer service responses to inquiries you submit;</li>
              <li>Service notifications related to the screen job you requested;</li>
              <li>Account notifications (for Screen Companies: new lead alerts, operational alerts, and account updates).</li>
            </ul>
            <p>
              <strong>Message frequency varies.</strong> Message and data rates may apply. You can reply <strong>STOP</strong> at any time to opt out of future messages, or <strong>HELP</strong> for help. After replying STOP, you will receive one final confirmation that you have been opted out.
            </p>
            <p>
              <strong>SMS consent is not shared with third parties or affiliates for marketing purposes.</strong> Mobile information you provide to enroll in WindowScreenQuote.com text messaging will not be shared with third parties or affiliates for marketing or promotional purposes. Information you submit through a Screen Company&rsquo;s funnel is shared only with that Screen Company so they can complete the screen job you requested, and only if you provide that information voluntarily.
            </p>
            <p>
              In addition to texts from WindowScreenQuote.com, the Screen Company that receives your quote may also contact you by phone, text, or email at the contact information you provided, even if your number is on a Do Not Call list, for the limited purpose of completing the screen job you requested. You can ask the Screen Company to stop contacting you at any time.
            </p>
            <p>
              For questions about our SMS practices, email <a href="mailto:support@windowscreenquote.com">support@windowscreenquote.com</a>.
            </p>
          </Section>

          <Section title="8. Cookies and similar technologies">
            <p>
              We use cookies and similar technologies (such as local storage) to keep you logged in, remember your preferences, secure the Service, and measure funnel performance. You can control cookies through your browser settings, but disabling them may prevent parts of the Service from working.
            </p>
          </Section>

          <Section title="9. Security">
            <p>
              We use reasonable technical and organizational measures to protect personal information, including encrypted transport, hashed passwords, and access controls. No system is perfectly secure, however, and we cannot guarantee absolute security. If we become aware of a breach that affects your information, we will notify you as required by law.
            </p>
          </Section>

          <Section title="10. Children">
            <p>
              The Service is not directed to children under 16, and we do not knowingly collect personal information from them. If you believe a child has provided us personal information, please contact us so we can delete it.
            </p>
          </Section>

          <Section title="11. International users">
            <p>
              We operate in the United States. If you use the Service from outside the United States, you understand that your information may be transferred to and processed in the United States, where data-protection laws may differ from those in your country.
            </p>
          </Section>

          <Section title="12. Changes to this Policy">
            <p>
              We may update this Policy from time to time. We will post the new version here with an updated &ldquo;Last updated&rdquo; date and, if the change is material, we&rsquo;ll provide additional notice. Your continued use of the Service after the change means you accept the updated Policy.
            </p>
          </Section>

          <Section title="13. Contact us">
            <p>
              Questions about this Policy or our privacy practices? Email us at <a href="mailto:privacy@windowscreenquote.com">privacy@windowscreenquote.com</a>.
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-6 border-t border-slate-200 text-sm text-slate-500 flex flex-wrap gap-5">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <Link to="/terms" className="hover:text-slate-900">Terms of Service</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
          <Link to="/login" className="hover:text-slate-900">Pro login</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-3">{title}</h2>
      <div className="text-slate-700 text-base leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
